// docs/34 SX4 — server cues. The module scores every pulse (and the table's own moments) into one
// `sfx_score` event row per batch: bank, variant, anchor, beat, delay, gain and pitch. This file
// receives those rows for the current World, keeps the ones for my party, and plays them:
//
//   anchor now           → at once (a ready, a ping, the pulse locking, the foe showing itself)
//   anchor entry         → when the playback reaches that log entry's clip, at its beat
//                          (start, impact, step n, end), scheduled on the audio clock
//   anchor afterPlayback → when that pulse's playback ends (the stinger, the next pulse, growls)
//   anchor firstTurn     → when my first planning turn of that encounter starts on screen (a
//                          hero's word as the fight opens, docs/83 PO1): `startFirstTurn`
//
// The client never re-rolls a server cue: the variant, gain and rate are the server's, so every
// player hears the same cackle at the same beat. Cues for a playback that never runs (no board on
// screen) skip their entry cues and still play what comes after.
import type { Identity } from 'spacetimedb';
import { tables, type DbConnection, type EventContext } from '../../bindings';
import type { SfxCueRow } from '../../bindings/types';
import { getConn, identityHex, onConnected } from '../net/connection';
import { useSession } from '../stores/session';
import { deviceNow } from '../time';
import { audio } from './index';
import { ANCHOR, beatOffset, type ClipBeats } from './mix';

interface ScoreRow { worldId: bigint; partyId: bigint; encounterId: bigint; pulseIndex: number; catalogue: number; cues: SfxCueRow[] }
interface PulseCues { entries: Map<number, SfxCueRow[]>; after: SfxCueRow[]; at: number }

const pending = new Map<string, PulseCues>();
/** docs/83 PO1: first-turn cues held per encounter, and the encounters whose first turn has begun. */
const firstTurn = new Map<string, { cues: SfxCueRow[]; at: number }>();
const turnBegun = new Set<string>();
const keyOf = (encounterId: bigint | number, pulse: number) => `${encounterId}:${pulse}`;
let warnedCatalogue = false;
let sub: { unsubscribe(): void } | undefined;
let unsubs: Array<() => void> = [];
let wiredWorld: bigint | undefined;

const gainOf = (c: SfxCueRow) => c.gainBp / 10_000;
const rateOf = (c: SfxCueRow) => c.rateBp / 10_000;

export function playCue(c: SfxCueRow, opts: { delayMs?: number; pan?: number; tag?: string; space?: number } = {}): void {
  audio.play(c.bank, {
    variant: c.variant, gain: gainOf(c), rate: rateOf(c), origin: 'server',
    delayMs: opts.delayMs ?? c.delayMs, pan: opts.pan, tag: opts.tag, space: opts.space,
    // a stinger or a long growl a little late still belongs (its first play may wait on a decode);
    // a footstep a little late does not
    maxLateMs: c.bank.startsWith('step.') ? 120 : (audio.bank(c.bank)?.variants[c.variant]?.ms ?? 0) > 1500 ? 1500 : 600,
  });
}

/** Is this batch for a party I am in (or the fight on my screen)? */
function mine(c: DbConnection, partyId: bigint): boolean {
  const me = useSession.getState().identity ?? '';
  for (const m of c.db.partyMember.iter()) if (m.partyId === partyId && identityHex(m.owner as Identity) === me) return true;
  return false;
}

/** Handles one `sfx_score` row (exported for tests and the dev harness). */
let received: string[] = [];
export function receive(row: ScoreRow, isMine = true): void {
  received.push(`${row.encounterId}:${row.pulseIndex}:${row.cues.length}:${isMine}`); if (received.length > 50) received = received.slice(-50);
  if (!isMine) return;
  if (row.catalogue !== audio.manifest.version && !warnedCatalogue) {
    warnedCatalogue = true;
    console.warn(`[sfx] the server's sound catalogue (${row.catalogue}) differs from this build's (${audio.manifest.version}); variants may not match other players'`);
  }
  let pc: PulseCues | undefined;
  for (const cue of row.cues) {
    if (cue.anchor === ANCHOR.now) { playCue(cue); continue; }
    if (row.encounterId === 0n) continue;
    if (cue.anchor === ANCHOR.firstTurn) {
      const k = String(row.encounterId);
      if (turnBegun.has(k)) { playCue(cue); continue; }
      const held = firstTurn.get(k) ?? { cues: [], at: deviceNow() };
      held.cues.push(cue);
      firstTurn.set(k, held);
      audio.prepare(cue.bank, cue.variant);
      continue;
    }
    pc ??= pending.get(keyOf(row.encounterId, row.pulseIndex)) ?? { entries: new Map(), after: [], at: deviceNow() };
    if (cue.anchor === ANCHOR.afterPlayback) pc.after.push(cue);
    else { const list = pc.entries.get(cue.seq) ?? []; list.push(cue); pc.entries.set(cue.seq, list); }
    audio.prepare(cue.bank, cue.variant);   // decode while the playback walks up to it
  }
  if (pc) pending.set(keyOf(row.encounterId, row.pulseIndex), pc);
  // forget scores nobody played (a fight left mid-playback)
  const stale = deviceNow() - 120_000;
  for (const [k, v] of pending) if (v.at < stale) pending.delete(k);
  for (const [k, v] of firstTurn) if (v.at < stale) firstTurn.delete(k);
}

/**
 * docs/83 PO1: my first planning turn of `encounterId` has started on screen (the HUD calls this
 * once the opening's cards, face-off and playback are done): the heroes' held words play now, and
 * any that arrive later play at once.
 */
export function startFirstTurn(encounterId: bigint | number): void {
  const k = String(encounterId);
  if (turnBegun.has(k)) return;
  turnBegun.add(k);
  if (turnBegun.size > 64) turnBegun.delete(turnBegun.values().next().value!);
  const held = firstTurn.get(k);
  firstTurn.delete(k);
  for (const c of held?.cues ?? []) playCue(c);
}

/** Encounters with first-turn cues still held (tests). */
export const heldFirstTurn = (): string[] => [...firstTurn.keys()];

/**
 * The cues of one playback clip (entry `seq` of pulse `pulse`): each lands on its beat, offset by
 * its delay, compressed with the playback speed. `panOf` places a unit on the board.
 */
export function playEntryCues(encounterId: number | bigint, pulse: number, seq: number, beats: ClipBeats, speed: number, panOf: (unit: number | undefined) => number | undefined, spaceOf?: (unit: number | undefined) => number | undefined): void {
  const list = pending.get(keyOf(encounterId, pulse))?.entries.get(seq);
  if (!list) return;
  for (const c of list) {
    const off = beatOffset(beats, c.beat, c.step);
    if (off === undefined) continue;
    // docs/92 HM7: what a huge body does rings in the hall
    playCue(c, { delayMs: off + c.delayMs / Math.max(1, speed), pan: panOf(c.unit), tag: tagOf(encounterId, pulse), space: spaceOf?.(c.unit) });
  }
}

export const tagOf = (encounterId: number | bigint, pulse: number): string => `pulse:${keyOf(encounterId, pulse)}`;

/** A skipped playback: the cues scheduled ahead of the cut stop. */
export function cancelPulseCues(encounterId: number | bigint, pulse: number): void { audio.cancel(tagOf(encounterId, pulse)); }

/** The pulse's playback has ended (or will never run): what follows it plays now. */
export function playAfterPlayback(encounterId: number | bigint, pulse: number): void {
  const k = keyOf(encounterId, pulse);
  const pc = pending.get(k);
  pending.delete(k);
  for (const c of pc?.after ?? []) playCue(c);
}

/** Pulses with cues waiting (tests). */
export const pendingPulses = (): string[] => [...pending.keys()];

function wire(c: DbConnection, worldId: bigint): void {
  sub?.unsubscribe();
  for (const u of unsubs) u();
  unsubs = [];
  sub = c.subscriptionBuilder()
    .onError((_ctx, err?: Error) => console.error('[sfx] subscription error', err))
    .subscribe([
      tables.sfxScore.where((r) => r.worldId.eq(worldId)),
      tables.partyMember.where((r) => r.worldId.eq(worldId)),
    ]);
  const onScore = (_ctx: EventContext, row: ScoreRow) => {
    if (row.worldId !== worldId) return;
    receive(row, mine(c, row.partyId));
  };
  c.db.sfxScore.onInsert(onScore);
  unsubs.push(() => c.db.sfxScore.removeOnInsert(onScore));
}

/** Follows the chosen World: subscribes to its scores while connected. Called once at boot. */
export function startServerCues(): void {
  (globalThis as unknown as Record<string, unknown>)['__fkSfxCues'] = { pending: pendingPulses, received: () => received, heldFirstTurn };   // e2e
  const follow = (worldId: bigint | undefined) => {
    if (worldId === wiredWorld) return;
    wiredWorld = worldId;
    sub?.unsubscribe(); sub = undefined;
    for (const u of unsubs) u();
    unsubs = [];
    if (worldId === undefined) return;
    const c = getConn();
    if (c) wire(c, worldId);
  };
  onConnected((c) => { if (wiredWorld !== undefined) wire(c, wiredWorld); });
  follow(useSession.getState().worldId);
  useSession.subscribe((s) => follow(s.worldId));
}
